import { timezoneMapping } from "../components/Timezone/Timezone.js"
import moment from "moment-timezone";
import ICAL from 'ical.js';
import config from '../web3/web3Config.json';
import constants from '../constants/constants.json'
import { reportError, toErrorString } from "../utils/errorLogger.js";

export const handleAttendees = (attendees) => {
    if (Array.isArray(attendees)) {
        return attendees.map(attendee => ({
            key: "guest",
            value: attendee?.value?.toString().split(":")[(attendee?.value?.toString().split(":").length - 1)] || null,
        }));
    } else if (attendees) {
        return [{

            key: "guest",
            value: attendees?.value?.toString().split(":")[(attendees?.value?.toString().split(":").length - 1)] || null,
        }];
    }
    return [];
}

export const convertingTimeFormat = (year, month, date, hour, minutes) => {
    const seconds = "00";
    const localDate = new Date(year, month, date, hour, minutes, seconds);
    const offsetInMilliseconds = new Date().getTimezoneOffset() * 60000;
    const utcDate = new Date(localDate.getTime() - offsetInMilliseconds);
    const inputDate = new Date(utcDate);
    const format = {
        year: "numeric",
        month: "long",
        day: "numeric",
        hour: "numeric",
        minute: "numeric",
        hour12: true,
    };
    const formatted_time = inputDate.toLocaleString("en-US", format);
    return formatted_time;
}

export const convertToUtc = (time, timezone) => {
    const convertedTime = time?.split("Z")[0];
    const timeZoneToUse = timezoneMapping[timezone] || timezone || 'Etc/UTC';

    return moment.tz(convertedTime, timeZoneToUse).utc().format();
}

export const formatDateTime = (dateTime) => ({
    year: dateTime.substring(0, 4),
    month: dateTime.substring(5, 7),
    date: dateTime.substring(8, 10),
    hour: dateTime.substring(11, 13),
    minute: dateTime.substring(14, 16),
});

export const getEventPrams = async (event, statusKey, start, end) => {
    const eventDetails = event?.event || {};
    const params = {
        uid: event.event.UID,
        title: event.event.SUMMARY?.value || event.event.SUMMARY || "",
        description: event.event.DESCRIPTION || event.event["DESCRIPTION:"] || "",
        fromTime: start || "",
        toTime: end || "",
        done: false,
        list: []
    };

    const additionalParams = [];

    if (eventDetails?.['X-APPOINTMENT-EVENT']) {
        additionalParams.push({
            key: "appointment",
            value: eventDetails?.['X-APPOINTMENT-EVENT'] || null,
        });
        params.appointmentEvent = true;
    }

    if (eventDetails.attach?.val) {
        additionalParams.push({
            key: "link",
            value: `${eventDetails.attach.val} ${eventDetails.attach.params.FILENAME || ''}`,
        });
    }

    if (eventDetails["X-GOOGLE-CONFERENCE"] || eventDetails["X-MICROSOFT-ONLINEMEETINGEXTERNALLINK"]) {
        additionalParams.push({
            key: "meetinglink",
            value: eventDetails["X-GOOGLE-CONFERENCE"] ||
                eventDetails["X-MICROSOFT-ONLINEMEETINGEXTERNALLINK"] ||
                null,
        });
    }
    if (eventDetails.ORGANIZER?.value?.split(":")[eventDetails.ORGANIZER?.value?.split(":").length - 1]) {
        additionalParams.push({
            key: "organizer",
            value: eventDetails.ORGANIZER.value.split(":")[eventDetails.ORGANIZER.value.split(":").length - 1],
        });
    }
    if (eventDetails["X-GUEST-PERMISSION"]) {
        additionalParams.push({
            key: "guest_permission",
            value: eventDetails["X-GUEST-PERMISSION"] === "MODIFY" ? "Modify event" : eventDetails["X-GUEST-PERMISSION"] === "INVITE" ? "View event" : "No access",
        });
    }
    if (eventDetails.LOCATION?.value || eventDetails.LOCATION) {
        additionalParams.push({
            key: "location",
            value: eventDetails.LOCATION?.value ?? eventDetails.LOCATION,
        });
    }
    if (eventDetails.CLASS) {
        additionalParams.push({
            key: "visibility",
            value: eventDetails.CLASS === "PUBLIC" ? "Public" : "Private",
        });
    }
    if (eventDetails.TRANSP) {
        additionalParams.push({
            key: "busy",
            value: eventDetails.TRANSP === "OPAQUE" ? "Busy" : "Free"
        })
    }

    additionalParams.push({
        key: "trigger",
        value: "-PT30M",
    }, {
        key: "notification",
        value: "Notification",
    });


    if (eventDetails.ATTENDEES) {
        additionalParams.push(...handleAttendees(eventDetails.ATTENDEES));
    }

    if (eventDetails?.RRULE) {
        // Parse RRULE once and destructure needed properties
        const rrule = Object.fromEntries(
            eventDetails.RRULE.split(';')
                .map(rule => rule.split('='))
        );

        const { WKST, FREQ, BYDAY, COUNT, INTERVAL, UNTIL, BYMONTHDAY, BYMONTH } = rrule;
        const frequency = FREQ?.toLowerCase();

        // Helper function to parse BYDAY
        const parseByday = () => {
            if (!BYDAY) return null;

            const numericPattern = /(?=.*\d)(?=.*[A-Za-z])/;
            if (numericPattern.test(BYDAY)) {
                const [week, day] = BYDAY.match(/(\d+)(\w+)/).slice(1);
                return { week, day: constants?.Days?.[day] };
            }
            return { value: BYDAY };
        };

        if (WKST || COUNT || INTERVAL || UNTIL || BYMONTHDAY || BYMONTH) {
            // Build custom event string
            const parts = [
                `unit~${frequency}`,
                BYDAY && (() => {
                    const bydayInfo = parseByday();
                    if (bydayInfo.day) {
                        return `customMonth~${bydayInfo.week}${bydayInfo.day}`;
                    }

                    return /^\d+$/.test(BYDAY)
                        ? `customMonth~${BYDAY}`
                        : `byday~${BYDAY}`;
                })(),
                BYMONTHDAY && `customMonth~${BYMONTHDAY}`,
                COUNT && `count~${COUNT}`,
                INTERVAL && `interval~${INTERVAL}`,
                UNTIL && `endDate~${moment(UNTIL?.toString(), 'YYYYMMDD').format('YYYY-MM-DD')}`,
                BYMONTH && `bymonth~${BYMONTH}`
            ].filter(Boolean);

            additionalParams.push(
                { key: "repeatEvent", value: 'custom' },
                { key: "customRepeatEvent", value: parts.join('_') }
            );

        } else {
            const bydayInfo = parseByday();
            const repeatEvent = bydayInfo?.day
                ? `${frequency}_${bydayInfo.week}${bydayInfo.day}`
                : BYDAY ? `${frequency}_${BYDAY}` : frequency;

            additionalParams.push({
                key: "repeatEvent",
                value: repeatEvent
            });
        }
    }

    if (eventDetails?.['RECURRENCE-ID']?.value) {
        params.uid = `${params?.uid}~${eventDetails?.['RECURRENCE-ID']?.value}`;
        params.singleEvent = true;
        params.recurrence_id = eventDetails?.['RECURRENCE-ID']?.value;
        additionalParams.push({
            key: "recurrence_id",
            value: eventDetails?.['RECURRENCE-ID']?.value
        })
    }

    params.list.push(...additionalParams.filter(param => param.value !== null));
    params.list.push({ key: statusKey, value: statusKey });
    return params;
}

// Function to clean and validate ICS content
const cleanIcsContent = (icsContent) => {
    if (!icsContent) return '';

    const lines = icsContent.split(/\r?\n/);
    const cleanedLines = [];

    for (const line of lines) {
        const trimmedLine = line.trim();

        // Skip empty lines
        if (!trimmedLine) {
            continue;
        }

        if (trimmedLine.includes(':') || trimmedLine.includes(';') ||
            trimmedLine.startsWith('BEGIN:') || trimmedLine.startsWith('END:') ||
            trimmedLine.startsWith(' ') || trimmedLine.startsWith('\t')) {
            cleanedLines.push(line);
        }
    }

    return cleanedLines.join('\n');
};

export const updateICS = async (decryptedFile, userName, acceptValue) => {
    return new Promise((resolve, reject) => {
        try {

            const email = verifyHostDomain(userName);
            let partStat = 'ACCEPTED';
            if (acceptValue === "accept") {
                partStat = "ACCEPTED";
            } else if (acceptValue === "maybe") {
                partStat = "TENTATIVE";
            } else {
                partStat = "DECLINED";
            }
            const reader = new FileReader();
            reader.onload = (e) => {
                try {
                    function formatDateToIcs(isoString) {
                        const date = new Date(isoString);
                        return date.toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';
                    }

                    const icsContent = e.target.result;
                    const cleanedIcsContent = cleanIcsContent(icsContent);
                    const parsedIcs = ICAL.parse(cleanedIcsContent);
                    const comp = new ICAL.Component(parsedIcs);
                    const vevent = comp.getFirstSubcomponent('vevent');
                    const cn = userName.split("@")[0];
                    const xNumGuests = '0';
                    const updatedAttendee = `ATTENDEE;CUTYPE=INDIVIDUAL;ROLE=REQ-PARTICIPANT;PARTSTAT=${partStat};RSVP=TRUE;CN=${cn};X-NUM-GUESTS=${xNumGuests}:mailto:${email}`;
                    const dtStart = formatDateToIcs(vevent.getFirstPropertyValue('dtstart'));
                    const dtEnd = formatDateToIcs(vevent.getFirstPropertyValue('dtend'));
                    const dtStamp = formatDateToIcs(vevent.getFirstPropertyValue('dtstamp'));
                    const organizer = vevent.getFirstPropertyValue('organizer');
                    const summary = vevent.getFirstPropertyValue('summary');
                    const description = vevent.getFirstPropertyValue('description');
                    const rule = vevent.getFirstPropertyValue('rrule');
                    const updatedIcsContent = [
                        "BEGIN:VCALENDAR",
                        "PRODID://DMail//Calendar//EN",
                        "VERSION:2.0",
                        "METHOD:REPLY",
                        "BEGIN:VEVENT",
                        `DTSTART:${dtStart}`,
                        `DTEND:${dtEnd}`,
                        `DTSTAMP:${dtStamp}`,
                        rule ? `RRULE:${rule}` : '',
                        `ORGANIZER;CN=${organizer.split(':')[1]}:${organizer}`,
                        `UID:${vevent.getFirstPropertyValue('uid')}`,
                        updatedAttendee,
                        `DESCRIPTION:${description}`,
                        "LOCATION:",
                        "SEQUENCE:0",
                        "STATUS:CONFIRMED",
                        `SUMMARY:${summary}`,
                        "TRANSP:OPAQUE",
                        "END:VEVENT",
                        "END:VCALENDAR"
                    ].join("\n");
                    const blob = new Blob([updatedIcsContent], { type: 'text/calendar' });
                    const file = new File([blob], 'invite.ics', { type: 'text/calendar' });
                    resolve({
                        file,
                        fileContent: updatedIcsContent
                    });
                } catch (error) {
                    const userName = localStorage.getItem('UserName');
                    const errorStr = toErrorString(error || error.message);
                    reportError({
                        endpoint: '-',
                        message: `FileName: commonHelper.js, Fun: updateICS, ${String(error.message || 'Unhandled error')}`,
                        error: errorStr,
                        userName: userName || '',
                    });
                    console.error('Error processing ICS content:', error);
                    if (error.message && error.message.includes('invalid line')) {
                        console.error('Invalid ICS format detected. The ICS file may contain malformed content.');
                        reject(new Error('Invalid ICS file format. Please ensure the calendar invitation is properly formatted.'));
                    } else {
                        reject(error);
                    }
                }
            };

            reader.onerror = (error) => {
                const userName = localStorage.getItem('UserName');
                const errorStr = toErrorString(error || error.message);
                reportError({
                    endpoint: '-',
                    message: `FileName: commonHelper.js, Fun: updateICS, ${String(error.message || 'Unhandled error')}`,
                    error: errorStr,
                    userName: userName || '',
                });
                console.error('Error reading file:', error);
                reject(error);
            };
            reader.readAsText(decryptedFile);
        } catch (error) {
            const userName = localStorage.getItem('UserName');
            const errorStr = toErrorString(error || error.message);
            reportError({
                endpoint: '-',
                message: `FileName: commonHelper.js, Fun: fetchLocation, ${String(error.message || 'Unhandled error')}`,
                error: errorStr,
                userName: userName || '',
            });
            console.error('Error in updateICS function:', error);
            reject(error);
        }
    });
};

export const getEmailContents = async (subject, status) => {
    let updatedSubject;

    switch (status) {
        case 'accept':
            updatedSubject = 'Your Invitation Has Been Accepted';
            break;
        case 'maybe':
            updatedSubject = 'Your Invitation Response is Tentative';
            break;
        case 'no':
            updatedSubject = 'Your Invitation Has Been Declined';
            break;
        default:
            updatedSubject = subject;
            break;
    }

    return updatedSubject;
};

export const fetchLocation = async (query) => {
    if (!query) return;

    try {
        const response = await fetch(`${config.LOCATION_URL}${query}`);
        const data = await response.json();

        const formattedSuggestions = data.map((location) => ({
            value: location.place_id,
            label: location.display_name,
            lat: location.lat,
            lon: location.lon,
        }));

        const suggestionsWithInput = [
            { value: query, label: query },
            ...formattedSuggestions
        ];
        return suggestionsWithInput;
    } catch (error) {
        const errorStr = toErrorString(error || error.message);
        const userName = localStorage.getItem('UserName');
        reportError({
            endpoint: '-',
            message: `FileName: commonHelper.js, Fun: fetchLocation, ${String(error.message || 'Unhandled error')}`,
            error: errorStr,
            userName: userName || '',
            level: 'warn',
        });
        console.error("Error fetching location suggestions:", error);
        return [{ value: query, label: query }];
    }
};

export const verifyHostDomain = (userName) => {
    return userName?.replace(config.DOMAIN, config.VERFIED_DOMAIN) || userName;
}

// Encode a string to hex
export function encodeToHex(str) {
    if (!str) return;
    return str.split('').map(c => c.charCodeAt(0).toString(16).padStart(2, '0')).join('');
}

// Decode a hex string to normal string
export function decodeFromHex(hexStr) {
    if (!hexStr) return;
    let str = '';
    for (let i = 0; i < hexStr.length; i += 2) {
        str += String.fromCharCode(parseInt(hexStr.substr(i, 2), 16));
    }
    return str;
}